import { useCallback, useRef, useState } from 'react';
import useDebouncedCallback from '@atlassian/jira-platform-use-debounce/src/utils/use-debounce-callback/index.tsx';
import { PrioritySchemesPageState } from '../../common/ui/types';
import { fetchPrioritySchemes } from './main';
import type { PrioritySchemeFilters, PrioritySchemesServiceData } from './types';

export type PrioritySchemesServiceReturn = {
	data: PrioritySchemesServiceData;
	state: (typeof PrioritySchemesPageState)[keyof typeof PrioritySchemesPageState];
	onRefetch: (page: number, filters: PrioritySchemeFilters) => void;
};

const initPrioritySchemesServiceData = {
	prioritySchemes: [],
	prioritySchemesCount: 0,
	taskInProgress: false,
};
export const usePrioritySchemesService = (): PrioritySchemesServiceReturn => {
	const [data, setData] = useState<PrioritySchemesServiceData>(initPrioritySchemesServiceData);
	const [state, setState] = useState<
		(typeof PrioritySchemesPageState)[keyof typeof PrioritySchemesPageState]
	>(PrioritySchemesPageState.INITITAL);
	// we need a stateRef, so that we can avoid adding an extra dependency for onRefetch method
	const stateRef = useRef(state);

	const originalRefetch = useCallback(async (page: number, filters: PrioritySchemeFilters) => {
		setState(PrioritySchemesPageState.LOADING);
		stateRef.current = PrioritySchemesPageState.LOADING;

		try {
			const response = await fetchPrioritySchemes(page, filters);
			setData(response);
			setState(PrioritySchemesPageState.SUCCESS);
			stateRef.current = PrioritySchemesPageState.LOADING;
		} catch (error) {
			setData(initPrioritySchemesServiceData);
			setState(PrioritySchemesPageState.ERROR);
			stateRef.current = PrioritySchemesPageState.LOADING;
		}
	}, []);

	const [debouncedRefetch] = useDebouncedCallback(originalRefetch, 300);

	const onRefetch = useCallback(
		async (page: number, filters: PrioritySchemeFilters) => {
			if (stateRef.current === PrioritySchemesPageState.INITITAL) {
				return originalRefetch(page, filters);
			}
			return debouncedRefetch(page, filters);
		},
		[debouncedRefetch, originalRefetch],
	);

	return {
		data,
		onRefetch,
		state,
	};
};
